import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken, getToken } from "@/lib/api";
import { AuthTransition } from "@/components/AuthTransition";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transition, setTransition] = useState({ active: false, mode: "login" });

  const playTransition = (mode = "login", holdMs = 6500) =>
    new Promise((resolve) => {
      setTransition({ active: true, mode });
      setTimeout(resolve, holdMs);
    });

  const endTransition = () => setTransition((t) => ({ ...t, active: false }));

  const fetchMe = useCallback(async () => {
    // No token => genuinely logged out.
    if (!getToken()) { setUser(false); setLoading(false); return; }
    // Restore the session. Only a real 401 (invalid/expired token) logs the user out.
    // Transient failures (network blip, backend cold start, 5xx) are retried so a VALID
    // session is never dropped — this was the cause of the "keeps asking to login" bug.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setLoading(false);
        return;
      } catch (e) {
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          clearToken();
          setUser(false);
          setLoading(false);
          return;
        }
        // Network error / 5xx: wait and retry (keep the token).
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          continue;
        }
        // Give up for now but DO NOT clear the token — a refresh will retry.
        setUser(false);
        setLoading(false);
        return;
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const { consumeGoogleRedirect } = await import("@/lib/firebase");
        const idToken = await consumeGoogleRedirect();
        if (idToken && !cancelled) {
          const { data } = await api.post("/auth/google", { id_token: idToken });
          if (data.access_token) setToken(data.access_token, true);
          window.location.replace("/app");
          return;
        }
      } catch {
        /* ignore redirect errors, fall back to normal session check */
      }
      if (!cancelled) fetchMe();
    };
    boot();
    return () => { cancelled = true; };
  }, [fetchMe]);

  const login = async (identifier, password, remember = true) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    if (data.access_token) setToken(data.access_token, remember);
    setUser(data.user);
    return data.user;
  };

  const register = async ({ name, email, password, username, phone }) => {
    const ref = localStorage.getItem("sfx_ref") || undefined;
    const { data } = await api.post("/auth/register", { name, email, password, username, phone, ref });
    if (data.access_token) setToken(data.access_token, true);
    localStorage.removeItem("sfx_ref");
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async () => {
    const { signInWithGoogle } = await import("@/lib/firebase");
    const idToken = await signInWithGoogle();
    if (!idToken) return null; // mobile redirect in progress; resumed on reload
    const { data } = await api.post("/auth/google", { id_token: idToken });
    if (data.access_token) setToken(data.access_token, true);
    setUser(data.user);
    return data.user;
  };

  const acceptInvite = async ({ token, username, phone, password }) => {
    const { data } = await api.post("/auth/accept-invite", { token, username, phone, password });
    if (data.access_token) setToken(data.access_token, true);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    // Clear per-user Assistant/support state so it never leaks to the next account on this device.
    localStorage.removeItem("sfx_asst_session");
    localStorage.removeItem("sfx_agent_mode");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, acceptInvite, logout, refreshUser: fetchMe, playTransition, endTransition }}>
      {children}
      <AuthTransition active={transition.active} mode={transition.mode} />
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
